import buildQrCodeOptions, { QrRenderType } from './buildQrCodeOptions';
import { QrConfig } from './types';

export interface GeneratedQr {
  blob: Blob;
  dataUrl: string;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Renders a QR code to a raster PNG blob (+ data URL for previewing).
 * Lazily imports qr-code-styling to keep it out of the main bundle chunk.
 */
export default async function generateQrImage(config: QrConfig): Promise<GeneratedQr> {
  const { default: QRCodeStyling } = await import('qr-code-styling');
  const qr = new QRCodeStyling(buildQrCodeOptions(config, 'canvas'));

  const raw = await qr.getRawData('png');
  if (!raw) {
    throw new Error('QR generation returned no data');
  }

  const blob = raw instanceof Blob ? raw : new Blob([raw as BlobPart], { type: 'image/png' });
  const dataUrl = await blobToDataUrl(blob);
  return { blob, dataUrl };
}

/** Renders a QR code to an SVG string (used for the "download SVG" action). */
export async function generateQrSvg(config: QrConfig): Promise<string> {
  const { default: QRCodeStyling } = await import('qr-code-styling');
  const qr = new QRCodeStyling(buildQrCodeOptions(config, 'svg'));
  const raw = await qr.getRawData('svg');
  if (!raw) {
    throw new Error('QR SVG generation returned no data');
  }
  if (typeof raw === 'string') {
    return raw;
  }
  return (raw as Blob).text();
}

export type { QrRenderType };
